<script setup lang="ts">
import { DismissIcon, LinkDismissIcon, MoreHorizontalIcon, OpenIcon } from '@blpde/nx-icons';
import { computed, inject, nextTick, onUnmounted, ref, useId, useTemplateRef, watch } from 'vue';
import { NxTextInput } from '.';
import { NxBadge } from '../Badge';
import { trackNxCodeCommit } from '../Composables/codeCommits';
import { NX_ENTER_COMMITTED_EVENT, NX_TAB_COMMITTED_EVENT } from '../Composables/enterAdvance';
import { focusFirstOf, isNxShown } from '../Composables/focusable';
import { NxDropdown } from './../Dropdown';
import { i18nInjectionKey } from '../internal';
import {
  BaseTextProps,
  type InputHeight,
  type InlineIconProps,
  FONT_SIZE_LOOKUP,
  isInTableCell,
} from './internal';
import {
  isPureNumericQuery,
  resolveCommitOption,
  type AutocompleteOption,
} from './autocompleteCommit';

const props = defineProps<
  BaseTextProps & {
    isLoading: boolean;
    options: Map<number, string>;
    showId?: boolean;
    /**
     * How search results should be filtered. Defaults to `startsWith`. `none`
     * shows `options` as they come: for a caller whose server search already
     * matched more than the display name (search name, partial number), so a
     * second filter here would drop valid hits.
     */
    searchMode?: 'startsWith' | 'contains' | 'none';
    /** Hint text in the empty field. */
    placeholder?: string;
    /** How many search results should be shown. Use `0` to show all matching options. Defaults to `5` */
    searchLimit?: number;
    searchIgnoreId?: boolean;
    searchIfEmpty?: boolean;
    searchOnFocus?: boolean;
    decoupled?: boolean;
    /**
     * Optional async search used on Enter when the user typed a pure numeric id
     * that is not yet in `options` (non-decoupled fields). Decoupled Artikel
     * ignores this: Enter only commits a highlighted Schnellsuche row.
     */
    resolveSearch?: (query: string) => void | Promise<void>;
    /**
     * Code resolution: the field takes a record's code (an account number,
     * „8400“) typed straight in. Tab or Enter with typed text and no row
     * picked from the list asks this first and commits its hit — the server
     * resolves the code, so the record need not be among the loaded options.
     * Tab then moves on to the next field — unless the field's Tab is
     * `always` (an NxTable edit cell): there the field keeps the focus and the
     * next Tab moves through the table. No hit (`null`) falls back to the
     * usual commit (a row of the list, a sole hit). With it a typed number is
     * never read as the record's id (as `searchIgnoreId`).
     */
    resolveCode?: (code: string) => Promise<{ id: number; name: string } | null>;
    /** Opens the related record. Receives the click, so a middle / Ctrl- / Cmd-click can open a new tab. */
    gotoRelation?: (event?: MouseEvent) => void;
    /** Opens the relation lookup table (more-button / F4). */
    onMoreButtonClick?: (payload: Event) => void;
    onDismiss?: (payload: PointerEvent) => void;
    additionalIcons?: InlineIconProps[];
    height?: InputHeight;
    /** Disables the input: greys it out and blocks typing, opening the dropdown and the inline icons (more/goto/dismiss). */
    disabled?: boolean;
    /**
     * Tab commit policy. Omitted ⇒ decided by where the field sits: `always`
     * in an NxTable cell, `leave` in a form (see `isInTableCell`). `always`
     * preventDefaults and commits — required for NxTable cell navigation.
     * `leave` commits exactly like `always` (the highlighted suggestion, a sole
     * hit) but lets the native Tab through, so a form field takes the pick and
     * the focus lands in the next field in one keystroke. `when-open` commits only while
     * the suggestion list is open *and* Tab would change the value: an
     * explicit highlight (arrows / option hover / click) or an exact typed
     * id/name that is not already committed. Closed list, no candidate, a
     * no-op (already-committed) candidate, and decoupled fields without a
     * pick all leave Tab native (plain forms / WCAG 2.1.2). Hover counts as
     * explicit intent, matching Enter, but only while the list is open.
     */
    commitOnTab?: 'always' | 'leave' | 'when-open';
    /**
     * When `true`, the built-in trailing icons (goto / more / dismiss) are
     * hidden until the field is hovered or focused, then fade in. In a narrow
     * table cell they are dropped entirely so the value is not clipped — F4 /
     * click / typing still open the lookup. Opt-in; defaults to `false`.
     */
    revealIcon?: boolean;
    /** Keep long plain-cell values clear of the trailing action rail. */
    reservePlainCellActionSpace?: boolean;
    /**
     * Hides the option's id column in the dropdown — for a caller whose ids are
     * internal positions rather than numbers a user knows (absent = shown).
     */
    hideOptionValue?: boolean;
    /**
     * A hint per option value, shown as a quiet neutral `NxBadge` (optically
     * centred for codes and numbers, cut with an ellipsis when narrow) between the
     * option's name and its id: for a caller whose server search matched a
     * value the row does not show (e.g. the article number `10045`), so the
     * user sees why the row is offered. Rows without an entry show none
     * (absent = no hints).
     */
    optionHints?: Map<number, string>;
    /**
     * For a `decoupled` field whose `onDismiss` handler empties the WHOLE field
     * (link, text and whatever hangs off them), not just the link: the dismiss
     * then shows the plain clear icon and tooltip instead of the link-dismiss
     * icon and its „the text stays“ tooltip, which would promise the opposite.
     * Nothing else of decoupled mode changes. A non-decoupled field already
     * shows the plain clear icon; the prop changes nothing there. Defaults to
     * `false`.
     */
    dismissClearsField?: boolean;
    /**
     * Opt-in: emit `typed` with the field's text on every user edit, in its
     * original case (`updatedSearch` lowercases and skips empty text). For a
     * `decoupled` field whose caller follows the typing — a free-text search
     * beside the picker — without waiting for the blur commit. The kit does not
     * debounce; the caller does. Nothing else changes: the name commits on blur
     * as before. Defaults to `false`.
     */
    emitTyped?: boolean;
    /**
     * Accessible name of the input itself (the tab stop). Opt-in: a fallthrough
     * `aria-label` stays on the dropdown root (the anchor), where existing
     * callers name the field group. This prop is the way to name the input.
     * Omitted, the input's name stays its placeholder / label as before.
     */
    inputAriaLabel?: string;
  }
>();

// If no element ID is given, an unique identifier is generated here and assigned to the id attribute
const elementId = props.id ?? useId();

const emit = defineEmits<{
  /** `source` distinguishes user typing from automatic focus/discovery search. */
  updatedSearch: [shadowNameValue: string, meta: { source: 'focus' | 'input' }];
  start: [];
  end: [];
  /**
   * The user explicitly selected an option of the OPEN list:
   * - a click on the option;
   * - Enter on the highlighted row (a coupled field highlights its first row at
   *   once, so Enter also picks that one);
   * - Tab, only with `commit-on-tab="when-open"`, on an EXPLICITLY highlighted row
   *   and only when that changes the field.
   * Fires after the label / id writes. For a click or Enter it ALSO fires when
   * they were no-ops (the same record picked again): the models then do not
   * change, so a consumer that has to react to „this record was picked“ (refill
   * what hangs off it) cannot see it any other way. A Tab that confirms the record
   * already selected is the no-op branch of commit-on-tab, returns first and fires
   * nothing: re-picking the same record is a click or Enter.
   * Never fires for a Tab that merely passes the field, a blur, or an Enter or
   * Tab with the list closed; a sole-hit or typed-id resolution without an
   * explicit selection is no pick. Nothing happens without a listener.
   */
  pick: [option: { id: number; name: string }];
  /**
   * Only with `emit-typed`: the text of the field after a user edit, exactly as
   * typed (original case, empty text too). Not debounced.
   */
  typed: [text: string];
}>();

defineExpose({
  focus: () => {
    document.getElementById(elementId)?.focus();
  },
});

const isDraggingOver = ref(false);
const queryStarted = ref(false);
const shadowNameValue = ref('');

// As long as the autocomplete value (!) does not get modified, this is the fallback
const idValueModel = defineModel<number | null, ''>('id_value', { required: true });
const nameValueModel = defineModel<string | null, ''>('name_value', {
  required: true,
  set(newValue) {
    shadowNameValue.value = newValue ?? '';
    return newValue;
  },
});

const isActive = ref(false);

/**
 * A code typed and left with Tab or Enter while the server still resolves it
 * (`commitTypedCode`): the field shows it, a blur keeps it, and the answer is
 * committed into this field when it arrives — unless the text changed or a
 * newer Tab or Enter took over (`token`).
 */
const pendingCode = ref<{ token: number; code: string } | null>(null);

/** A typed number is no id: by request, or because the field resolves codes. */
const ignoreId = computed(() => !!props.searchIgnoreId || !!props.resolveCode);

/** Numeric FK when `showId` is on — ignore null/undefined/NaN. */
const displayId = computed(() => {
  const id = idValueModel.value;
  if (id === null || id === undefined) return null;
  const n = typeof id === 'number' ? id : Number(id);
  return Number.isFinite(n) ? n : null;
});

const inputModel = computed<string>({
  get() {
    // While focused the user types the name only (ID is not part of the query).
    if (isActive.value || isDraggingOver.value || pendingCode.value) {
      return shadowNameValue.value;
    }

    const name = (nameValueModel.value ?? '').trim();
    const id = !props.decoupled && props.showId ? displayId.value : null;

    if (name && id != null) return `${name} (${id})`;
    if (name) return name;
    // ID without resolved label still visible (e.g. label still loading).
    if (id != null) return String(id);
    return '';
  },
  set(newValue) {
    shadowNameValue.value = newValue;
    if (props.emitTyped) emit('typed', newValue);
  },
});

/** A decoupled dismiss drops the link only; `dismissClearsField` hosts empty everything. */
const decoupledDismiss = computed(() => props.decoupled && !props.dismissClearsField);

const showLoadingMessage = computed(
  () =>
    props.isLoading &&
    (props.searchLimit === 0 || relevantOptions.value.length < (props.searchLimit ?? 5))
);
const showNoResultsError = computed(() => !relevantOptions.value.length);

const isOpen = computed(() => {
  if (!isActive.value || !queryStarted.value || !(shadowNameValue.value || props.searchIfEmpty)) {
    return false;
  }

  return !props.decoupled || (!showLoadingMessage.value && !showNoResultsError.value);
});

/**
 * Focus copies the committed label into the shadow query, then marks the field
 * active. The watcher is pre-flush, so it would otherwise run after `isActive`
 * and search that label. The copy is not typing; `searchOnFocus` owns the
 * focus search. The stored string is that copy. The next watcher run skips
 * the emit only when the shadow is still that string — a same-tick overwrite
 * leaves the string set, and a different value must still search.
 */
let focusSyncedShadow: string | null = null;

watch(shadowNameValue, () => {
  const synced = focusSyncedShadow;
  focusSyncedShadow = null;
  if (synced !== null && shadowNameValue.value === synced) return;
  if (isActive.value && (shadowNameValue.value || props.searchIfEmpty)) {
    emit('updatedSearch', shadowNameValue.value.toLowerCase(), { source: 'input' });
  }
});

watch(nameValueModel, (newValue) => {
  shadowNameValue.value = newValue ?? '';
});

const relevantOptions = computed(() => {
  const newOptions: { name: string; value: number }[] = [];
  const addNewOption = (optionValue: number, optionName: string): boolean => {
    if (props.searchLimit === 0 || newOptions.length < (props.searchLimit ?? 5)) {
      newOptions.push({ name: optionName, value: optionValue });
      return true;
    }

    return false;
  };

  if (props.searchMode === 'none') {
    for (const [optionValue, optionName] of props.options) {
      if (!addNewOption(optionValue, optionName)) break;
    }
    return newOptions;
  }

  const searchValue = String(shadowNameValue.value).toLowerCase();
  // Option names are typed `string`, but a relation whose display field is
  // numeric (e.g. a tax rate) hands us numbers at runtime — coerce before
  // string ops so the autocomplete doesn't crash on `.toLowerCase()`.
  let matchFn = (optionName: string) => String(optionName).toLowerCase().startsWith(searchValue);
  let additionalMatchFn: (optionName: string) => boolean = () => false;

  if (props.searchMode === 'contains') {
    additionalMatchFn = (optionName: string) =>
      String(optionName).toLowerCase().includes(searchValue);
  }

  if (searchValue) {
    if (!ignoreId.value && Number(searchValue).toFixed() === searchValue) {
      const optionValue = Number(searchValue);
      const optionName = props.options.get(optionValue);
      if (optionName) {
        addNewOption(optionValue, optionName);
      }
    }
  } else {
    matchFn = () => props.searchIfEmpty;
    additionalMatchFn = () => false;
  }

  const additionalMatches: [number, string][] = [];
  for (const [optionValue, optionName] of props.options) {
    if (!matchFn(optionName)) {
      if (!additionalMatchFn(optionName)) {
        continue;
      }

      additionalMatches.push([optionValue, optionName]);
      continue;
    }

    if (!addNewOption(optionValue, optionName)) break;
  }
  for (const [optionValue, optionName] of additionalMatches) {
    if (!addNewOption(optionValue, optionName)) break;
  }

  return newOptions;
});

const i18nInjection = inject(i18nInjectionKey)!;
let mousedown = false;
/** True when the highlight came from arrows / option hover / click, not
 *  the automatic moveFocusToFirstOption (focus + every keystroke). */
let highlightIsExplicit = false;

const selectedOptionIndex = ref(-1);
const dropdownRef = useTemplateRef('dropdown-ref');

const onDragenter = () => {
  // Overwrite shadow value and set it as display value
  shadowNameValue.value = nameValueModel.value ?? '';

  isDraggingOver.value = true;
};

const onDragleave = () => {
  isDraggingOver.value = false;
};

const onInputStart = () => {
  if (mousedown || props.disabled) return;

  // Overwrite shadow value and set it as display value. Not a typed search:
  // see `focusSyncedShadow`.
  const committed = nameValueModel.value ?? '';
  // A code still being resolved after Tab or Enter stays what the field shows.
  if (shadowNameValue.value !== committed && !pendingCode.value) {
    focusSyncedShadow = committed;
    shadowNameValue.value = committed;
  }

  isActive.value = true;
  isDraggingOver.value = false;

  emit('start');
  highlightIsExplicit = false;

  if (props.searchOnFocus) {
    moveFocusToFirstOption();
    emit('updatedSearch', shadowNameValue.value.toLowerCase(), { source: 'focus' });
  }
};

const moveFocusToOption = (
  optionIndex: number,
  scrollIntoView: boolean,
  smoothScrollBehavior = true
) => {
  queryStarted.value = true;
  listDismissed = false;
  selectedOptionIndex.value = optionIndex;

  if (scrollIntoView) {
    if (optionIndex > -1) {
      // Wait for the view to update the "active" class
      nextTick(() => {
        if (dropdownRef.value) {
          const activeOptionElement = dropdownRef.value.querySelector('.active');
          activeOptionElement?.scrollIntoView({
            behavior: smoothScrollBehavior ? 'smooth' : undefined,
            block: 'center',
          });
        }
      });
    } else if (dropdownRef.value) {
      // Index is below zero = Simply scroll to the top of the dropdown
      dropdownRef.value.scrollTop = 0;
    }
  }
};

const moveFocusToFirstOption = () => {
  highlightIsExplicit = false;
  moveFocusToOption(props.decoupled ? -1 : 0, true, true);
};

/**
 * Typing. A pick closes the list without a blur (`closeList`), so the field keeps
 * the focus but is inactive: it would show the committed label and ignore what is
 * typed next. The user's own typing in the focused field starts a new edit; an
 * input event of a field without the focus (autofill, a host writing the text) does not.
 */
const onTextInput = () => {
  if (
    !isActive.value &&
    !props.disabled &&
    document.activeElement === document.getElementById(elementId)
  ) {
    isActive.value = true;
    // The pick belongs to the edit that is over: a blur must judge the new text.
    selectedSomething = false;
  }
  moveFocusToFirstOption();
};

/** IME keydown uses keyCode 229 on some engines even when `isComposing` is late. */
const isImeKey = (e: KeyboardEvent) => e.isComposing || e.keyCode === 229;

const moveFocusToPreviousOption = (e: KeyboardEvent) => {
  // Vue .up does not skip composition — check before highlight / preventDefault.
  if (isImeKey(e) || !isOpen.value) return;
  e.preventDefault();
  e.stopPropagation();
  highlightIsExplicit = true;
  moveFocusToOption(
    (Math.max(selectedOptionIndex.value - 1, -1) + relevantOptions.value.length) %
      relevantOptions.value.length,
    true
  );
};

const moveFocusToNextOption = (e: KeyboardEvent) => {
  if (isImeKey(e) || !isOpen.value) return;
  e.preventDefault();
  e.stopPropagation();
  highlightIsExplicit = true;
  moveFocusToOption(
    (selectedOptionIndex.value + 1 + relevantOptions.value.length) % relevantOptions.value.length,
    true
  );
};

const onOptionHover = (optionIndex: number) => {
  // Hover is explicit intent, same as arrows / click / Enter. Incidental
  // pointer motion over an open dropdown can therefore commit on Tab —
  // only while the list is open; closeList() makes the next Tab native.
  highlightIsExplicit = true;
  moveFocusToOption(optionIndex, false);
};

let selectedSomething = false;
/** Bumps on each Enter so a late resolveSearch cannot commit into another cell. */
let enterSeq = 0;

const applyCommit = (selectedOption: AutocompleteOption) => {
  // Skip no-op writes so tabbing through an unchanged field does not dirty the row.
  if (idValueModel.value !== selectedOption.value || nameValueModel.value !== selectedOption.name) {
    // ORDER IS A CONTRACT: label first, FK second. The ERP's relation atom holds
    // the picked label locally (it must never reach the record — a display key
    // like `country.name` would be PATCHed onto the LINKED master-data row,
    // renaming it for everyone: I-1030) and ties it to the FK written right
    // after. Swap these two lines and that binding runs before the label exists,
    // so the freshly picked label is dropped again on the spot. Note the
    // condition above: this branch is also taken when ONLY the label differs, so
    // the FK write is not always a change — see `useRelationLabel`
    // (`frontend/erp/src/components/section/relationLabel.ts`).
    nameValueModel.value = selectedOption.name;
    idValueModel.value = selectedOption.value;
  }
  selectedSomething = true;
};

const closeList = () => {
  // Close the list without blurring: a blur here races NxTable's
  // lazy-edit exit (cursor cleared before the cell can jump to Menge).
  isActive.value = false;
  queryStarted.value = false;
  highlightIsExplicit = false;
};

/**
 * Escape on an open list (I-2510): the list goes, the field stays active —
 * typing searches again, an arrow reopens the list — and the next Tab picks
 * nothing (`listDismissed`); the blur then keeps or reverts the typed text as
 * any blur without a pick does.
 */
let listDismissed = false;
const dismissList = () => {
  listDismissed = true;
  queryStarted.value = false;
  highlightIsExplicit = false;
};

const setSelected = (opts?: { allowSoleHit?: boolean; pick?: boolean }) => {
  // Judged BEFORE the commit and before the list closes: a pick is an explicit
  // selection of the highlighted row of the open list, nothing else.
  const explicitRow =
    opts?.pick && isOpen.value && selectedOptionIndex.value > -1
      ? relevantOptions.value[selectedOptionIndex.value]
      : undefined;
  const selectedOption = resolveCommitOption({
    options: props.options,
    relevantOptions: relevantOptions.value,
    selectedOptionIndex: selectedOptionIndex.value,
    shadowValue: shadowNameValue.value,
    searchIgnoreId: ignoreId.value,
    allowSoleHit: opts?.allowSoleHit ?? true,
  });
  if (selectedOption) applyCommit(selectedOption);
  if (selectedOption && explicitRow && selectedOption === explicitRow) {
    emit('pick', { id: selectedOption.value, name: selectedOption.name });
  }
  closeList();
};

/** Exact typed id already in `options`, or a unique exact name among the
 *  filtered rows — not an automatic first-option highlight. */
const exactTypedHit = (): AutocompleteOption | null => {
  const raw = String(shadowNameValue.value).trim();
  if (!raw) return null;
  if (!ignoreId.value && isPureNumericQuery(raw) && props.options.has(Number(raw))) {
    return { name: String(props.options.get(Number(raw)) ?? ''), value: Number(raw) };
  }
  const exact = relevantOptions.value.filter((option) => String(option.name) === raw);
  const hit = exact.length === 1 ? exact[0] : undefined;
  return hit ? { name: String(hit.name), value: hit.value } : null;
};

/** The typed text a Tab or Enter resolves as a code, or `null` (see `resolveCode`). */
const typedCode = (): string | null => {
  if (!props.resolveCode || props.decoupled || highlightIsExplicit || listDismissed) return null;
  const raw = String(shadowNameValue.value).trim();
  if (!raw || raw === String(nameValueModel.value ?? '').trim()) return null;
  return raw;
};

/** The next stop of the Tab order after `from`, as a native Tab would reach it. */
const focusNextTabStop = (from: HTMLElement) => {
  const stops = [
    ...document.querySelectorAll<HTMLElement>(
      'a[href], button, input, select, textarea, [tabindex]'
    ),
  ].filter(
    (el) =>
      el.tabIndex >= 0 &&
      !el.matches(':disabled') &&
      !(el instanceof HTMLInputElement && el.type === 'hidden') &&
      // Hidden by an attribute or by CSS (`v-show`, an ancestor's display: none).
      isNxShown(el)
  );
  // The first stop after `from` that really takes the focus (review
  // BLP/nx#3198 171906, finding 5).
  focusFirstOf(stops.slice(stops.indexOf(from) + 1));
};

/**
 * Tab or Enter on a typed code: the server resolves it, then the hit is
 * committed (no hit: the usual commit).
 *
 * In a form, Tab and Enter move on AT ONCE and the answer is committed into
 * this field when it arrives (`pendingCode`): holding the focus until then
 * sent whatever was typed meanwhile into this field instead of the next one,
 * and the code was lost with it (Christine 29.10.2026, BLP/nx#3198). Tab
 * focuses the next stop — unless a form cancels the `nx-tab-committed` the
 * field reports first and moves the cursor itself once the field settled;
 * Enter reports `nx-enter-committed` from the field
 * right away, so a form that moves on with Enter or takes a row over goes on
 * as if Enter had passed. Whatever leaves the record or takes the row over
 * waits for such commits first (`nxCodeCommitsSettled`).
 *
 * In an NxTable cell the table moves and reads the cell at once, so both keys
 * are held there until the answer (Tab: `commitOnTab` `always`).
 */
const commitTypedCode = async (e: KeyboardEvent, code: string) => {
  e.preventDefault();
  e.stopPropagation();
  const input = e.target instanceof HTMLElement ? e.target : null;
  const isTab = e.key === 'Tab';
  const token = ++enterSeq;
  const movesOnAtOnce =
    !!input &&
    (isTab
      ? (props.commitOnTab ?? (isInTableCell(input) ? 'always' : 'leave')) !== 'always'
      : !isInTableCell(input));

  if (movesOnAtOnce) {
    pendingCode.value = { token, code };
    if (isTab) {
      const reported = new CustomEvent(NX_TAB_COMMITTED_EVENT, { bubbles: true, cancelable: true });
      if (input.dispatchEvent(reported)) focusNextTabStop(input);
    } else input.dispatchEvent(new CustomEvent(NX_ENTER_COMMITTED_EVENT, { bubbles: true }));
    await resolvePendingCode(token, code);
    return;
  }

  const hit = await resolveCodeSafely(code);
  // Drop late answers (the user left the field / typed something else).
  if (token !== enterSeq || !isActive.value || String(shadowNameValue.value).trim() !== code) {
    return;
  }
  if (hit) {
    applyCommit({ value: hit.id, name: hit.name });
    closeList();
  } else {
    setSelected({ allowSoleHit: true, pick: !isTab });
  }
  if (!input || isTab) return;
  input.dispatchEvent(new CustomEvent(NX_ENTER_COMMITTED_EVENT, { bubbles: true }));
};

/** The server's answer for a typed code; a failed lookup counts as no hit. */
const resolveCodeSafely = async (code: string) => {
  try {
    return (await props.resolveCode?.(code)) ?? null;
  } catch (err) {
    console.error('autocomplete resolveCode failed', err);
    return null;
  }
};

/** Resolves a code marked pending and commits it into this field, tracked for `nxCodeCommitsSettled`. */
const resolvePendingCode = (token: number, code: string): Promise<void> => {
  const commit = resolveCodeSafely(code).then((hit) => commitPendingCode(token, code, hit));
  trackNxCodeCommit(commit);
  return commit;
};

/**
 * Ctrl+Enter (Cmd+Enter) on a typed code: the form finishes its record or row
 * with it, so the key passes on untouched — the field only starts resolving the
 * code as a pending one, and the form awaits `nxCodeCommitsSettled` before it
 * reads the value (BLP/nx#3198). Not in an NxTable cell, which reads at once.
 */
const startCodeOnCtrlEnter = (e: KeyboardEvent) => {
  const code = typedCode();
  const input = e.target instanceof HTMLElement ? e.target : null;
  if (!code || !input || isInTableCell(input)) return;
  const token = ++enterSeq;
  pendingCode.value = { token, code };
  void resolvePendingCode(token, code);
};

/** The answer to a code left with Tab or Enter, committed into this field (see `commitTypedCode`). */
const commitPendingCode = (
  token: number,
  code: string,
  hit: { id: number; name: string } | null
) => {
  const current = pendingCode.value?.token === token;
  if (current) pendingCode.value = null;
  // A newer Tab or Enter took over, or the text changed meanwhile.
  if (!current || String(shadowNameValue.value).trim() !== code) return;
  if (hit) {
    applyCommit({ value: hit.id, name: hit.name });
    closeList();
  } else {
    setSelected({ allowSoleHit: true });
  }
  if (document.activeElement !== document.getElementById(elementId)) {
    // What a blur does after its commit: the field shows the committed value.
    shadowNameValue.value = nameValueModel.value ?? '';
    selectedSomething = false;
  }
};

/** Enter: await id lookup if needed, then commit (see resolveCommitOption). */
const onEnterKey = async (e: KeyboardEvent) => {
  // Confirming CJK composition must not assign a product / commit a row.
  if (isImeKey(e)) return;
  const code = typedCode();
  if (code) {
    await commitTypedCode(e, code);
    return;
  }
  // Artikel (decoupled): only a highlighted Schnellsuche row. Typing an id
  // or a unique prefix must not jump to Menge without an explicit pick.
  if (props.decoupled) {
    const highlighted =
      selectedOptionIndex.value > -1 ? relevantOptions.value[selectedOptionIndex.value] : undefined;
    if (!highlighted) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    setSelected({ allowSoleHit: false, pick: true });
    return;
  }

  const raw = String(shadowNameValue.value).trim();
  if (
    !ignoreId.value &&
    isPureNumericQuery(raw) &&
    !props.options.has(Number(raw)) &&
    props.resolveSearch
  ) {
    // Hold the event so table-cell navigation doesn't steal focus mid-fetch.
    e.preventDefault();
    e.stopPropagation();
    const token = ++enterSeq;
    const typed = raw;
    try {
      await props.resolveSearch(raw);
    } catch (err) {
      console.error('autocomplete resolveSearch failed', err);
    }
    // Drop late resolves (user left the cell / typed something else / re-entered).
    if (token !== enterSeq || !isActive.value || String(shadowNameValue.value).trim() !== typed) {
      return;
    }
    setSelected({ allowSoleHit: true, pick: true });
    return;
  }
  setSelected({ allowSoleHit: true, pick: true });
};

/** Arrow/Enter without Vue .up/.down/.enter so IME can be checked first. */
const onNavKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && !isImeKey(e)) {
    startCodeOnCtrlEnter(e);
    return;
  }
  if (e.ctrlKey || e.altKey || e.metaKey || e.shiftKey) return;
  if (e.key === 'Escape' && isOpen.value) {
    // Escape closes an open list first: nothing is picked, the field keeps
    // focus. Only a second Escape reaches the cell, the section or a dialog.
    e.preventDefault();
    e.stopPropagation();
    dismissList();
    return;
  }
  if (e.key === 'ArrowUp') {
    moveFocusToPreviousOption(e);
    return;
  }
  if (e.key === 'ArrowDown') {
    moveFocusToNextOption(e);
    return;
  }
  if (e.key === 'Enter') void onEnterKey(e);
};

/** Tab confirms a commit candidate. `always` preventDefaults (NxTable cell
 * nav walks icons even when defaultPrevented). `leave` (forms) commits the
 * same and lets native Tab move on: `setSelected` marks the pick, so the blur
 * that follows keeps it instead of reverting. `when-open` preventDefaults
 * only when a commit actually happens — explicit highlight or exact typed
 * id/name that is not already committed; otherwise native Tab leaves the field. */
const onTabKey = (e: KeyboardEvent) => {
  if (e.shiftKey) return;
  // The list was dismissed with Escape: Tab moves on without a pick.
  if (listDismissed) return;
  const code = typedCode();
  if (code) {
    void commitTypedCode(e, code);
    return;
  }
  const mode =
    props.commitOnTab ?? (isInTableCell(e.target as Element | null) ? 'always' : 'leave');
  if (mode === 'always' || mode === 'leave') {
    if (mode === 'always') e.preventDefault();
    setSelected({ allowSoleHit: true });
    return;
  }
  // Closed list → native Tab. closeList() after a commit takes this path,
  // so a second Tab is never trapped.
  if (!isOpen.value) return;
  // Decoupled (Artikel): Tab must not commit a typed id/name without a pick.
  if (props.decoupled && !highlightIsExplicit) return;
  const selectedOption = highlightIsExplicit
    ? resolveCommitOption({
        options: props.options,
        relevantOptions: relevantOptions.value,
        selectedOptionIndex: selectedOptionIndex.value,
        shadowValue: shadowNameValue.value,
        searchIgnoreId: ignoreId.value,
        allowSoleHit: true,
      })
    : exactTypedHit();
  if (!selectedOption) return;
  // A no-op commit is not a reason to swallow Tab (saved field whose label
  // is already in options; second Tab after a successful commit while open).
  if (
    selectedOption.value === idValueModel.value &&
    String(selectedOption.name) === String(nameValueModel.value ?? '')
  ) {
    return;
  }
  e.preventDefault();
  const highlightedRow =
    highlightIsExplicit && selectedOptionIndex.value > -1
      ? relevantOptions.value[selectedOptionIndex.value]
      : undefined;
  applyCommit(selectedOption);
  // Only the explicitly highlighted row is a pick; an exact typed id / name is not.
  // The no-op branch above returns without one: a Tab that passes an unchanged field.
  if (highlightedRow && highlightedRow === selectedOption) {
    emit('pick', { id: selectedOption.value, name: selectedOption.name });
  }
  closeList();
};

/**
 * F4 = open the relation lookup table (classic ERP search-help).
 * Only when the more-button is wired; otherwise F4 falls through.
 */
const onOpenTableShortcut = (e: KeyboardEvent) => {
  if (props.disabled || !props.onMoreButtonClick) return;
  e.preventDefault();
  e.stopPropagation();
  // Collapse the inline dropdown so it does not sit under the modal.
  queryStarted.value = false;
  props.onMoreButtonClick(e);
};

// const onWindowFocusIn = () => {
//   window.removeEventListener('mouseup', onWindowMouseUp);
//   mousedown = false;
//   onInputFinish();
// };

const onWindowMouseup = () => {
  mousedown = false;
  // A click-pick already committed — don't steal focus back from Menge.
  if (selectedSomething) return;
  document.getElementById(elementId)?.focus();
};

const onDropdownMousedown = () => {
  mousedown = true;
  window.addEventListener('mouseup', onWindowMouseup, { once: true });
  // window.addEventListener('focusin', onWindowFocusIn, { once: true });
};

const onSelectStart = (optionIndex: number) => {
  // Ensure the selected option has the visible focus
  highlightIsExplicit = true;
  moveFocusToOption(optionIndex, false);
};

const onInputFinish = () => {
  if (mousedown) return;

  isActive.value = false;
  listDismissed = false;
  // A code left with Tab or Enter and still resolving is kept: its answer commits it.
  const holdsPendingCode =
    pendingCode.value !== null && String(shadowNameValue.value).trim() === pendingCode.value.code;
  // Invalidate in-flight Enter resolveSearch so a blur/refocus cannot commit a
  // stale lookup into this (or a recycled) cell.
  if (!holdsPendingCode) enterSeq += 1;

  if (props.decoupled) {
    if (!selectedSomething && nameValueModel.value !== shadowNameValue.value) {
      nameValueModel.value = shadowNameValue.value;
    }
  } else if (!selectedSomething && !holdsPendingCode) {
    // If nothing got selected, revert — unless the typed value is an exact
    // name match or a pure numeric id that is already in options (id + blur).
    const raw = String(shadowNameValue.value).trim();
    const idHit = !ignoreId.value && isPureNumericQuery(raw) && props.options.has(Number(raw));
    const exactNameHit =
      !!raw &&
      relevantOptions.value.length > 0 &&
      shadowNameValue.value === relevantOptions.value[selectedOptionIndex.value]?.name &&
      relevantOptions.value.filter((option) => option.name === shadowNameValue.value).length === 1;

    if (idHit || exactNameHit) {
      // Blur path: never auto-link a sole prefix match — only exact id/name.
      setSelected({ allowSoleHit: false });
    }

    shadowNameValue.value = nameValueModel.value ?? '';
  }

  queryStarted.value = false;
  selectedSomething = false;
  highlightIsExplicit = false;
  emit('end');
};

onUnmounted(() => {
  if (mousedown) {
    window.removeEventListener('mouseup', onWindowMouseup);
    // window.removeEventListener('focusin', onWindowFocusIn);
  }
});

/**
 * Middle click on "Öffnen" = the related record in a new tab; `gotoRelation`
 * decides. The default is suppressed first: over a button the middle button
 * otherwise starts the browser's autoscroll, and the user is left with a
 * scroll cursor on the mask they just left.
 */
function gotoRelationOnMiddleClick(event: MouseEvent): void {
  if (event.button !== 1) return;
  event.preventDefault();
  props.gotoRelation?.(event);
}
</script>

<template>
  <NxDropdown :is-open="isOpen" @close="isOpen = false">
    <template #trigger>
      <NxTextInput
        :id="elementId"
        v-model="inputModel"
        :escape-restores="false"
        :placeholder="props.placeholder"
        :disabled="props.disabled"
        :aria-label="props.inputAriaLabel"
        :icons="[
          ...(props.gotoRelation
            ? [
                {
                  component: OpenIcon,
                  size: '1.25em' as const,
                  align: 'end' as const,
                  color: 'var(--nxt-brand-bg)',
                  filled: true,
                  label: i18nInjection.t('common.input.actions.goto'),
                  tooltip: i18nInjection.t('common.input.actions.goto_tooltip'),
                  onClick: props.gotoRelation,
                  onAuxClick: gotoRelationOnMiddleClick,
                  revealOnInteraction: props.revealIcon,
                } satisfies InlineIconProps,
              ]
            : []),
          ...(props.onMoreButtonClick
            ? [
                {
                  component: MoreHorizontalIcon,
                  size: '1.5em',
                  align: 'end',
                  color: 'var(--nxt-brand-bg)',
                  filled: false,
                  label: i18nInjection.t('common.input.actions.open_table'),
                  tooltip: i18nInjection.t('common.input.actions.open_table_tooltip'),
                  onClick: props.onMoreButtonClick,
                  revealOnInteraction: props.revealIcon,
                } satisfies InlineIconProps,
              ]
            : []),
          ...(idValueModel !== null && props.onDismiss
            ? [
                {
                  component: decoupledDismiss ? LinkDismissIcon : DismissIcon,
                  size: '1.25em',
                  align: 'end',
                  color: 'var(--nxt-brand-bg)',
                  filled: true,
                  // Short name for aria; longer tooltip explains the decoupled case
                  // (link gone, free text stays) so the action is still clear later.
                  label: i18nInjection.t('common.input.actions.clear'),
                  tooltip: i18nInjection.t(
                    decoupledDismiss
                      ? 'common.input.actions.clear_decoupled_tooltip'
                      : 'common.input.actions.clear_tooltip'
                  ),
                  onClick: props.onDismiss,
                  revealOnInteraction: props.revealIcon,
                } satisfies InlineIconProps,
              ]
            : []),
          ...(props.additionalIcons ?? []),
        ]"
        :data-focus-visible="isOpen ? true : undefined"
        :height="props.height"
        :reserve-plain-cell-action-space="props.reservePlainCellActionSpace"
        @focus="onInputStart"
        @input.passive="onTextInput"
        @keydown="onNavKeydown"
        @keydown.tab="onTabKey"
        @keydown.exact.f4="onOpenTableShortcut"
        @blur="onInputFinish"
        @dragenter="onDragenter"
        @dragleave="onDragleave"
        @dblclick="props.gotoRelation"
      />
    </template>
    <div
      ref="dropdown-ref"
      :class="{ 'dropdown-overflow': props.searchLimit !== undefined }"
      :tabindex="-1"
      :style="`--font-size: ${FONT_SIZE_LOOKUP[props.height ?? 's']}`"
      @mousedown="onDropdownMousedown"
    >
      <div
        v-for="(autocompleteOption, optionIndex) in relevantOptions"
        :key="autocompleteOption.value"
        :class="{ active: optionIndex === selectedOptionIndex }"
        @mousemove.passive="onOptionHover(optionIndex)"
        @mousedown.prevent="onSelectStart(optionIndex)"
        @click.stop="setSelected({ pick: true })"
      >
        <div v-text="autocompleteOption.name" />
        <div v-if="props.optionHints?.get(autocompleteOption.value)" class="autocomplete-hint">
          <NxBadge variant="neutral" size="xs" truncate cap-centered>
            {{ props.optionHints.get(autocompleteOption.value) }}
          </NxBadge>
        </div>
        <div v-if="!props.hideOptionValue" v-text="autocompleteOption.value" />
      </div>
      <div
        v-if="showLoadingMessage"
        class="autocomplete-message"
        v-text="
          relevantOptions.length
            ? i18nInjection.t('common.autocomplete.partially_loading')
            : i18nInjection.t('common.autocomplete.loading')
        "
      />
      <div
        v-else-if="showNoResultsError"
        class="autocomplete-error autocomplete-message"
        v-text="i18nInjection.t('common.autocomplete.no_results')"
      />
    </div>
  </NxDropdown>
</template>

<style scoped>
.autocomplete-message {
  font-size: 0.875em;
  font-family: var(--nxt-font);
  font-weight: var(--nxt-font-weight-medium);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
}

.autocomplete-error {
  color: var(--nxt-danger-bg);
}

:deep(.nx-dropdown-popover) > div {
  font-size: var(--font-size);
  border-radius: var(--nxt-radius-md);
  padding: var(--nxt-space-2xs);
  background-color: var(--nxt-popover-bg);
  border: solid var(--nxt-hairline) var(--nxt-popover-border);
  box-shadow: var(--nxt-popover-shadow);
  user-select: none;

  &.dropdown-overflow {
    max-height: 10em;
    overflow-y: scroll;
  }

  > div:not(.autocomplete-message) {
    color: var(--nxt-menu-item-fg);
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: var(--nxt-space-xs) var(--nxt-space-sm);
    border-radius: var(--nxt-radius-sm);
    cursor: pointer;
    background-color: transparent;

    gap: var(--nxt-space-xs);

    &.active {
      background-color: var(--nxt-menu-item-bg-active);
      color: var(--nxt-menu-item-fg);
    }

    > div {
      font-size: 0.875em;
      font-family: var(--nxt-font);
      font-weight: var(--nxt-font-weight-semibold);

      &:first-child {
        flex-grow: 1;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      /* The id column only: with `hideOptionValue` the name is the last child too. */
      &:last-child:not(:first-child) {
        font-weight: var(--nxt-font-weight-medium);
        color: var(--nxt-fg-muted);
      }

      /* `optionHints`: a quiet neutral badge set apart from the name; in a
         narrow list it shrinks and the badge cuts its text with an ellipsis. */
      &.autocomplete-hint {
        display: flex;
        align-items: center;
        min-width: 0;
        overflow: hidden;
        margin-inline-start: var(--nxt-space-md);
      }
    }
  }
}
</style>
